import React, { useEffect, useRef, useState } from 'react';
import { X, ZoomIn } from 'lucide-react';
import { trackEvent } from '../../lib/tracking';

const RyadFloorPlansSimple = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Close lightbox on ESC key + lock body scroll
  useEffect(() => {
    if (!selectedPlan) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setSelectedPlan(null);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [selectedPlan]);

  const plans = [
    {
      id: 1,
      type: "F2",
      title: "F2 - 60 m²",
      subtitle: "1 Chambre + 1 Salon + 2 SDB",
      image: "/F2-B.png",
      features: ["Chambre parent: 15,90 m²", "Salon: 24,87 m²", "SDB: 5,30 m²", "2ème SDB: 3,05 m²", "Cuisine: 4,90 m²", "Balcon: 4,58 m²"],
      badge: "Idéal investissement"
    },
    {
      id: 2,
      type: "F2",
      title: "F2 - 42,36 m²",
      subtitle: "1 Chambre + 1 Salon",
      image: "https://customer-assets.emergentagent.com/job_sogi-realestate/artifacts/ay30ajm3_APPART%20F2-43%20m%C2%B2.jpg",
      features: ["Chambre: 13,50 m²", "Salon: 17,39 m²", "Salle de bains: 2,77 m²"],
      badge: "Compact"
    },
    {
      id: 3,
      type: "F3",
      title: "F3 - 88,35 m²",
      subtitle: "2 Chambres + 1 Salon",
      image: "/F3-B.png",
      features: ["Chambre parents: 13,60 m²", "Chambre: 11,55 m²", "Salon: 18,30 m²", "Cuisine: 5,70 m²", "SDB: 3,35 m²", "Hall: 6,25 m²", "TV: 3,15 m²", "Dégagement: 2,25 m²", "Terrasse: 4,90 m² + 2,50 m²"],
      badge: "Familial"
    },
    {
      id: 4,
      type: "F4",
      title: "F4 B - 141 m²",
      subtitle: "3 Chambres + 1 Salon",
      image: "/F4-B.png",
      features: ["Chambre parent: 20,90 m²", "Chambre 2: 11,75 m²", "Chambre 3: 10,60 m²", "Salon: 29,30 m²", "Cuisine: 5,50 m²", "SDB: 4,40 m²", "SDB: 3,65 m²", "SDB: 2,95 m²", "Espace familial: 6,20 m²", "Buanderie: 2,20 m²", "TV: 3,50 m²", "Entrée: 7,50 m²", "Terrasse: 5,30 m² + 1,60 m²"],
      badge: "Spacieux"
    },
    {
      id: 5,
      type: "F4",
      title: "F4 C - 135 m²",
      subtitle: "3 Chambres + 1 Salon",
      image: "/F4-C.png",
      features: ["Chambre parent: 17,20 m²", "Chambre: 11,30 m²", "Chambre: 12,60 m²", "Salon: 23,00 m²", "Cuisine: 8,37 m²", "SDB: 3,95 m²", "SDB: 3,97 m²", "Espace familial: 6,60 m²", "Entrée: 11,75 m²", "TV: 2,00 m²", "Dégagement: 3,60 m²", "Terrasse: 10,30 m²"],
      badge: "Premium"
    }
  ];

  return (
    <section id="plans" ref={sectionRef} className="py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-20 left-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-0 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-6 tracking-wide uppercase">
            Nos surfaces
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            <span className="gradient-text">Plans des Appartements</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Découvrez les plans détaillés de nos différents types d'appartements
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <div 
              key={plan.id}
              data-apartment-type={plan.type}
              className={`group bg-white rounded-3xl shadow-lg overflow-hidden border border-gray-100 hover:border-amber-200 transition-all duration-500 card-hover transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Image Container */}
              <button
                type="button"
                onClick={() => {
                  setSelectedPlan(plan);
                  trackEvent('ViewContent', {
                    content_type: 'product',
                    content_name: plan.title,
                    content_category: plan.subtitle,
                    content_ids: [String(plan.id)],
                  });
                }}
                data-testid={`open-plan-${plan.id}`}
                aria-label={`Agrandir le plan ${plan.title}`}
                className="relative aspect-[4/3] overflow-hidden bg-gray-50 w-full block cursor-zoom-in focus:outline-none focus:ring-4 focus:ring-amber-400/40"
              >
                <img
                  src={plan.image}
                  alt={plan.title}
                  className="w-full h-full object-contain transform group-hover:scale-105 transition-transform duration-700"
                />
                {/* Badge */}
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full shadow-lg">
                    {plan.badge}
                  </span>
                </div>
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2">
                    <ZoomIn className="w-4 h-4 text-white" />
                    <p className="text-white text-sm font-medium">Cliquez pour agrandir</p>
                  </div>
                </div>
              </button>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-amber-600 transition-colors">
                    {plan.title}
                  </h3>
                </div>
                <p className="text-amber-600 font-medium mb-4">{plan.subtitle}</p>
                
                {/* Features */}
                <div className="space-y-3">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-sm text-gray-600 group-hover:text-gray-700 transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center mr-3 group-hover:bg-amber-100 transition-colors">
                        <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                      </div>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox modal */}
      {selectedPlan && (
        <div
          data-testid="plan-lightbox"
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 animate-fade-in-up"
          onClick={() => setSelectedPlan(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`Plan ${selectedPlan.title}`}
        >
          <button
            type="button"
            data-testid="close-plan-lightbox"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedPlan(null);
            }}
            className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition backdrop-blur-sm z-10"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>

          <div
            className="relative max-w-6xl w-full max-h-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedPlan.image}
              alt={selectedPlan.title}
              className="max-h-[80vh] w-auto max-w-full object-contain rounded-xl shadow-2xl"
            />
            <div className="mt-5 text-center">
              <p className="text-white text-xl md:text-2xl font-semibold">{selectedPlan.title}</p>
              <p className="text-amber-300 text-sm md:text-base mt-1">{selectedPlan.subtitle}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default RyadFloorPlansSimple;
